import type {
  CategoryId,
  ContractDocument,
  ContractResponse,
  ContractStatus,
  ContractType,
} from '@timelog/pm-shared';
import {
  clearRateCard,
  rateCardAllowedFor,
  setCategoryAmount,
  setCategoryFactor,
  setCategoryHidden,
  setOwnHours,
} from '@timelog/pm-shared';

/**
 * The Financials editor's client state — ADR 0010 re-applied to the contract
 * aggregate: one reducer owns the ACTIVE contract's document while the page is
 * mounted; inline edits buffer inside cells and only COMMIT here. Siblings
 * stay in the query cache; the provider overlays this state onto the list.
 */

export type ContractSaveStatus = 'saved' | 'dirty' | 'saving' | 'error' | 'conflict';

/**
 * What is being edited, as one flat key — e.g. 'name', 'revenue',
 * 'amount:exp', 'factor:own', 'own-hours', 'split-amount:exp:<rowId>',
 * 'rate:<entryId>'. A string keeps the many small cell identities out of the
 * reducer's type surface.
 */
export type ContractEditKey = string;

export type ContractEditorState = {
  contractId: string;
  name: string;
  type: ContractType;
  status: ContractStatus;
  /** Authoritative while editing; server state is only adopted explicitly. */
  document: ContractDocument;
  /** Bumped on every mutation; drives the autosave debounce. */
  revision: number;
  /** The version the next PUT must carry (BR-20). */
  serverVersion: number;
  savedRevision: number;
  saveStatus: ContractSaveStatus;
  editingKey: ContractEditKey | null;
};

export type ContractEditorAction =
  | { type: 'RESET_FROM_SERVER'; contract: ContractResponse }
  | { type: 'SET_NAME'; name: string }
  | { type: 'SET_TYPE'; contractType: ContractType }
  | { type: 'SET_REVENUE'; revenue: number }
  | { type: 'SET_CATEGORY_AMOUNT'; id: CategoryId; amount: number }
  | { type: 'SET_CATEGORY_FACTOR'; id: CategoryId; factor: number }
  /**
   * `factor` is the budget-model config's default for this section — since
   * schema 2 it rides the flag payload, so the dispatcher supplies it and the
   * reducer stays config-free (`CATEGORY_DEFS` is no longer the source).
   */
  | { type: 'RESET_CATEGORY_FACTOR'; id: CategoryId; factor: number }
  | { type: 'SET_OWN_HOURS'; hours: number }
  | { type: 'SET_CATEGORY_HIDDEN'; id: CategoryId; hidden: boolean }
  /**
   * A pure shared transform (split ops, rate-card ops, flow seeds) applied to
   * the reducer-current document — components never close over a stale one.
   */
  | { type: 'APPLY_OP'; op: (document: ContractDocument) => ContractDocument }
  | { type: 'START_EDITING'; key: ContractEditKey }
  | { type: 'STOP_EDITING' }
  | { type: 'SAVE_STARTED' }
  | { type: 'SAVE_SUCCEEDED'; version: number; savedRevision: number }
  | { type: 'SAVE_FAILED' }
  | { type: 'SAVE_CONFLICT' }
  | { type: 'RESOLVE_CONFLICT_KEEP_MINE'; version: number }
  | { type: 'ADOPT_SERVER'; contract: ContractResponse };

export const createInitialContractState = (contract: ContractResponse): ContractEditorState => ({
  contractId: contract.id,
  name: contract.name,
  type: contract.type,
  status: contract.status,
  document: contract.document,
  revision: 0,
  serverVersion: contract.version,
  savedRevision: 0,
  saveStatus: 'saved',
  editingKey: null,
});

/** Any mutation the PUT body carries: revision bump + dirty (conflict is sticky). */
const withDirty = (
  state: ContractEditorState,
  patch: Partial<Pick<ContractEditorState, 'name' | 'type' | 'document'>>,
): ContractEditorState => ({
  ...state,
  ...patch,
  revision: state.revision + 1,
  saveStatus: state.saveStatus === 'conflict' ? 'conflict' : 'dirty',
});

const withDocument = (
  state: ContractEditorState,
  document: ContractDocument,
): ContractEditorState => (document === state.document ? state : withDirty(state, { document }));

export const contractReducer = (
  state: ContractEditorState,
  action: ContractEditorAction,
): ContractEditorState => {
  switch (action.type) {
    case 'RESET_FROM_SERVER':
      return createInitialContractState(action.contract);

    case 'SET_NAME': {
      const name = action.name.trim();
      if (name === '' || name === state.name) return state;
      return withDirty(state, { name });
    }

    case 'SET_TYPE':
      // BR-11/D10: only time-based contracts carry a rate card — switching to
      // fixed price drops it, or the API would refuse the next save.
      return action.contractType === state.type
        ? state
        : withDirty(state, {
            type: action.contractType,
            ...(rateCardAllowedFor(action.contractType)
              ? {}
              : { document: clearRateCard(state.document) }),
          });

    case 'SET_REVENUE': {
      const revenue = Math.max(0, Math.round(action.revenue));
      if (revenue === state.document.revenue) return state;
      return withDocument(state, { ...state.document, revenue });
    }

    case 'SET_CATEGORY_AMOUNT':
      return withDocument(state, setCategoryAmount(state.document, action.id, action.amount));

    // Same write, two intents: reset carries the config's default for the
    // section rather than a typed value, and stays named for what it is.
    case 'SET_CATEGORY_FACTOR':
    case 'RESET_CATEGORY_FACTOR':
      return withDocument(state, setCategoryFactor(state.document, action.id, action.factor));

    case 'SET_OWN_HOURS':
      return withDocument(state, setOwnHours(state.document, action.hours));

    case 'SET_CATEGORY_HIDDEN':
      return withDocument(state, setCategoryHidden(state.document, action.id, action.hidden));

    case 'APPLY_OP':
      return withDocument(state, action.op(state.document));

    case 'START_EDITING':
      return { ...state, editingKey: action.key };

    case 'STOP_EDITING':
      return state.editingKey === null ? state : { ...state, editingKey: null };

    case 'SAVE_STARTED':
      return { ...state, saveStatus: 'saving' };

    case 'SAVE_SUCCEEDED':
      // Contracts have no server-assigned fields (unlike plan codes, BR-6) —
      // only the version is adopted; the local document stays authoritative.
      return {
        ...state,
        serverVersion: action.version,
        savedRevision: action.savedRevision,
        saveStatus: state.revision === action.savedRevision ? 'saved' : 'dirty',
      };

    case 'SAVE_FAILED':
      return { ...state, saveStatus: 'error' };

    case 'SAVE_CONFLICT':
      return { ...state, saveStatus: 'conflict' };

    case 'RESOLVE_CONFLICT_KEEP_MINE':
      // Adopt the fresh version, mark dirty — the autosave loop re-PUTs ours.
      return { ...state, serverVersion: action.version, saveStatus: 'dirty' };

    case 'ADOPT_SERVER':
      return { ...createInitialContractState(action.contract), editingKey: null };

    default:
      return state;
  }
};
